<template>
    <div class="tyn-root">
        <Header />
        <div class="tyn-content tyn-content-page">
            <div class="tyn-main">
                <div class="tyn-section pt-0 pb-0">

                    <div v-if="flowCompleted" class="completion-container">
                        <div class="completion-bg"></div>
                        <div class="completion-card" :class="flowCompletedVariantClass">
                            <div class="completion-icon">
                                <svg xmlns="http://www.w3.org/2000/svg"
                                     viewBox="0 0 48 48"
                                     aria-hidden="true"
                                     fill="none"
                                     stroke="currentColor"
                                     stroke-width="3"
                                     stroke-linecap="round"
                                     stroke-linejoin="round">
                                    <circle cx="24" cy="24" r="20" />
                                    <polyline points="15 24.5 21 30.5 33 18" />
                                </svg>
                                <span class="completion-spark"></span>
                            </div>
                            <div class="completion-content">
                                <h3>{{ flowCompletedTitle || 'Artefato pronto!' }}</h3>
                                <p>{{ flowCompletedMessage || 'O documento foi enviado para a próxima etapa do fluxo.' }}</p>
                                <div class="completion-actions">
                                    <button class="btn-solid" @click="irParaListaProcessos">
                                        Voltar para Processos
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Erro -->
                    <div v-else-if="errorMsg" class="blocker">
                        <h3>Este artefato já está em continuidade</h3>
                        <p class="motivo">{{ errorMsg }}</p>
                        <button class="btn" @click="irParaListaProcessos">Voltar para Processos</button>
                    </div>

                    <div v-else class="viewer-layout">
                        <div v-if="isDocxView" class="docx-wrapper" :class="{ 'is-loading': editorBusy }">
                            <div v-if="editorBusy" class="editor-overlay">
                                <div class="overlay-content">
                                    <svg class="spinner" viewBox="0 0 50 50" aria-label="Carregando">
                                        <circle cx="25" cy="25" r="20" fill="none" stroke-width="5" />
                                    </svg>
                                    <p>Carregando documento…</p>
                                </div>
                            </div>

                            <ejs-documenteditorcontainer ref="docx"
                                                         :serviceUrl="`${urlApi}/DOCXEditor/`"
                                                         :enableToolbar="!editorBusy"
                                                         :enableTrackChanges="true"
                                                         :currentUser="nomeUsuarioEditor"
                                                         height="100%"
                                                         width="100%"
                                                         locale="pt-BR"
                                                         @created="onEditorCreated"
                                                         @beforeXmlHttpRequestSend="beforeSend"
                                                         @toolbarClick="onToolbarClick"
                                                         @trackChange="onTrackChange"
                                                         @beforeAcceptRejectChanges="onBeforeAcceptRejectChanges" />
                        </div>

                        <div v-else-if="isPdfView" class="pdf-wrapper">
                            <div ref="pdfviewer" class="pdf-host"></div>
                        </div>

                        <aside class="side-panel">
                            <div class="panel-header">
                                <div class="header-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                        <polyline points="14 2 14 8 20 8"></polyline>
                                    </svg>
                                </div>
                                <div class="header-content">
                                    <h3 class="titulo-artefato">
                                        {{ processoArtefato?.nmModeloArtefato || 'Artefato' }}
                                    </h3>
                                    <p class="sub">
                                        {{ processoArtefato?.dsNomeProcesso }}
                                    </p>
                                    <span v-if="processoArtefato?.cdProcesso" class="codigo-badge">
                                        #{{ processoArtefato.cdProcesso }}
                                    </span>
                                </div>
                            </div>

                            <div class="info-card status-card">
                                <div class="card-title">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <polyline points="12 6 12 12 16 14"></polyline>
                                    </svg>
                                    Status Atual
                                </div>
                                <div class="status-badge">
                                    {{ processoArtefato?.nmStatusAtual || '—' }}
                                </div>
                            </div>

                            <div class="info-card details-card">
                                <div class="card-title">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="12" y1="16" x2="12" y2="12"></line>
                                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                    </svg>
                                    Detalhes
                                </div>
                                <div class="info-grid">
                                    <div class="info-item" v-if="processoArtefato?.dsLoginResponsavel">
                                        <span class="label">Responsável</span>
                                        <span class="value">{{ processoArtefato.dsLoginResponsavel }}</span>
                                    </div>
                                    <div class="info-item" v-if="processoArtefato?.dtInclusaoStatus">
                                        <span class="label">Atualizado em</span>
                                        <span class="value">{{ formatarData(processoArtefato.dtInclusaoStatus) }}</span>
                                    </div>
                                </div>
                            </div>

                            <PainelDestinatariosEtapa
                                modo="owner"
                                :idProcessoArtefato="processoArtefato?.idProcessoArtefato || $route.params.id"
                                :idProcesso="processoArtefato?.idProcesso"
                                :idProcessoModeloArtefato="processoArtefato?.idProcessoModeloArtefato"
                                :cdStatusAtual="processoArtefato?.cdTipoStatusHistorico"
                                :nextStep="nextStep" />

                            <div class="acoes-section">
                                <button v-if="isDocxView"
                                        class="btn-action btn-outline-primary mb-2"
                                        @click="salvarEdicao"
                                        :disabled="loading || editorBusy">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor"
                                         class="bi bi-save" viewBox="0 0 16 16">
                                        <path d="M2 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4.5L10.5 1H2zm9 2v3H5V3h6z" />
                                        <path d="M4.5 9.5A1.5 1.5 0 1 1 6 11a1.5 1.5 0 0 1-1.5-1.5z" />
                                    </svg>
                                    <span>Salvar alterações</span>
                                </button>
                                <button v-if="nextStep !== 'assinatura'"
                                        class="btn-action btn-success"
                                        @click="darContinuidade"
                                        :disabled="loading">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor"
                                         class="bi bi-arrow-bar-up" viewBox="0 0 16 16">
                                        <path fill-rule="evenodd"
                                              d="M8 10a.5.5 0 0 0 .5-.5V3.707l2.146 2.147a.5.5 0 0 0 .708-.708l-3-3a.5.5 0 0 0-.708 0l-3 3a.5.5 0 1 0 .708.708L7.5 3.707V9.5a.5.5 0 0 0 .5.5m-7 2.5a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 0 1h-13a.5.5 0 0 1-.5-.5" />
                                    </svg>
                                    <span>{{ nextButtonLabel }}</span>
                                </button>
                                <button v-if="isDocxView && nextStep === 'assinatura'"
                                        class="btn-action btn-primary"
                                        @click="prepararEEnviarParaAssinatura"
                                        :disabled="loading || editorBusy">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor"
                                         class="bi bi-pen" viewBox="0 0 16 16">
                                        <path d="m13.498.795.149-.149a1.207 1.207 0 0 1 1.707 1.707l-.149.149a1.5 1.5 0 0 1-.39.272l-8.813 8.814a.5.5 0 0 1-.168.11l-3.39 1.356a.25.25 0 0 1-.316-.316l1.356-3.39a.5.5 0 0 1 .11-.168l8.814-8.813a1.5 1.5 0 0 1 .29-.229zM11.854 2.146 3.707 10.293l-.647 1.618 1.618-.647L13.854 3.354l-2-2z" />
                                    </svg>
                                    <span>Enviar para Assinatura</span>
                                </button>
                                <button v-if="isPdfView  && nextStep === 'assinatura'"
                                        class="btn-action btn-primary"
                                        @click="abrirModalAssinantes"
                                        :disabled="loading">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor"
                                         class="bi bi-pen" viewBox="0 0 16 16">
                                        <path d="m13.498.795.149-.149a1.207 1.207 0 0 1 1.707 1.707l-.149.149a1.5 1.5 0 0 1-.39.272l-8.813 8.814a.5.5 0 0 1-.168.11l-3.39 1.356a.25.25 0 0 1-.316-.316l1.356-3.39a.5.5 0 0 1 .11-.168l8.814-8.813a1.5 1.5 0 0 1 .29-.229zM11.854 2.146 3.707 10.293l-.647 1.618 1.618-.647L13.854 3.354l-2-2z" />
                                    </svg>
                                    <span>Enviar para Assinatura</span>
                                </button>

                            </div>

                        </aside>
                    </div>

                </div>
            </div>
        </div>
        <Modais />
        <ModalEscolhaAssinantes :idProcessoArtefato="+$route.params.id"
                                :nmModeloArtefato="processoArtefato?.nmModeloArtefato"
                                :nmProcesso="processoArtefato?.dsNomeProcesso"
                                :idProcessoModeloArtefato="processoArtefato?.idProcessoModeloArtefato"
                                @assinatura-gerada="onAssinaturaGerada" />
        <ModalEscolhaEnvolvidos ref="modalEnvolvidos"
                                :idProcessoArtefato="+($route.params.id || 0)"
                                :idProcesso="processoArtefato?.idProcesso"
                                :idProcessoModeloArtefato="processoArtefato?.idProcessoModeloArtefato"
                                :modo="modalEnvolvidosModo"
                                @confirmar="onEnvolvidosConfirm" />
    </div>
</template>

<script>
    import Header from '@/components/Header'
    import Modais from '@/components/Modais'
    import { urlApi, avisoToast, avisoErro } from '@/config/global'
    import axios from 'axios'
    import Swal from 'sweetalert2'
    import { L10n, setCulture } from '@syncfusion/ej2-base'
    import ptBR from '@syncfusion/ej2-locale/src/pt-BR.json'
    import { PdfViewer, Navigation, Magnification, Print, TextSelection, LinkAnnotation, BookmarkView, ThumbnailView } from '@syncfusion/ej2-pdfviewer'
    import ModalEscolhaAssinantes from '../components/ModalEscolhaAssinantes.vue'
    import ModalEscolhaEnvolvidos from '../components/ModalEscolhaEnvolvidos.vue'
    import PainelDestinatariosEtapa from '../components/PainelDestinatariosEtapa.vue'

    PdfViewer.Inject(Toolbar, Navigation, Magnification, Print, TextSelection, LinkAnnotation, BookmarkView, ThumbnailView)

    // Document Editor (Vue)
    import {
        DocumentEditorContainerComponent as EjsDocumenteditorcontainer,
        Toolbar, SpellChecker
    } from '@syncfusion/ej2-vue-documenteditor'

    import { uploadFileToS3 } from '@/services/fileUploadService'
    L10n.load(ptBR); setCulture('pt-BR')

    export default {
        name: 'ProcessoRevisado',
        components: { Header, Modais, ModalEscolhaAssinantes, ModalEscolhaEnvolvidos, PainelDestinatariosEtapa, 'ejs-documenteditorcontainer': EjsDocumenteditorcontainer },
        provide: { DocumentEditorContainer: [Toolbar, SpellChecker] },
        data() {
            return {
                processoArtefato: null,
                loading: false,
                errorMsg: null,
                urlApi,
                editorBusy: false,
                consolidandoRevisoes: false,
                flowCompleted: false,
                flowCompletedTitle: '',
                flowCompletedMessage: '',
                flowCompletedVariant: 'success',
                modalEnvolvidosModo: 'revisao',
            }
        },
        computed: {
            editor() {
                return this.$refs.docx?.ej2Instances?.documentEditor || null
            },
            sufixoPapelEditor() {
                return ' · Responsável'
            },
            nomeUsuarioEditor() {
                const u = this.$store.state.user
                let nome = 'Usuário'
                if (u?.usuarioApi?.nmUsuario) nome = u.usuarioApi.nmUsuario
                else if (u?.usuario?.nmUsuario) nome = u.usuario.nmUsuario
                return `${nome}${this.sufixoPapelEditor}`
            },

            flowCompletedVariantClass() {
                return this.flowCompletedVariant === 'primary'
                    ? 'completion-card-primary'
                    : 'completion-card-success'
            },

            nextStep() {
                const dto = this.processoArtefato || {}
                const fase = (dto.tpFaseAtual || '').toLowerCase()
                const flRevisao = !!dto.flRevisao
                const flAprovacao = !!dto.flAprovacao
                const flAssinatura = !!dto.flAssinatura
                if (fase === 'enviado') {
                    if (flRevisao) return 'revisao'
                    if (flAprovacao) return 'aprovacao'
                    if (flAssinatura) return 'assinatura'
                    return 'finalizar'
                }

                if (['revisado', 'aguardando reenvio'].includes(fase)) {
                    if (flAprovacao) return 'aprovacao'
                    if (flAssinatura) return 'assinatura'
                    return 'finalizar'
                }

                if (fase === 'aprovado') {
                    if (flAssinatura) return 'assinatura'
                    return 'finalizar'
                }

                if (fase === 'assinado') {
                    return 'finalizar'
                }
                return 'finalizar'
            },


            nextButtonLabel() {
                switch (this.nextStep) {
                    case 'revisao': return 'Enviar para Revisão'
                    case 'aprovacao': return 'Enviar para Aprovação'
                    case 'assinatura': return 'Enviar para Assinatura'
                    default: return 'Finalizar'
                }
            },

            isEnviado() {
                const fase = this.processoArtefato?.tpFaseAtual
                return fase?.toLowerCase() === 'enviado'
            },

            isRevisado() {
                const fase = this.processoArtefato?.tpFaseAtual
                return ['revisado', 'aguardando reenvio'].includes(fase?.toLowerCase())
            },

            isAprovado() {
                const fase = this.processoArtefato?.tpFaseAtual
                return fase?.toLowerCase() === 'aprovado'
            },

            isDocxView() {
                const dto = this.processoArtefato || {}
                if (this.isRevisado) {
                    const extRev = this.getExt(dto.urlArquivoRevisado)
                    return extRev !== 'pdf'
                } else if (this.isEnviado ){
                    const extRev = this.getExt(dto.urlArquivo)
                    return extRev !== 'pdf'
                }
                return this.isEnviado
            },
            isPdfView() {
                const dto = this.processoArtefato || {}
                if (this.isRevisado || this.isAprovado) {
                    return this.getExt(dto.urlArquivoRevisado) === 'pdf'
                }
                if (this.isEnviado) {
                    return this.getExt(dto.urlArquivo) === 'pdf'
                }
                return false
            },

        },
        watch: {
            editorBusy(val) {
                const c = this.$refs.docx?.ej2Instances
                if (!c) return
                if (val && c.showSpinner) c.showSpinner()
                if (!val && c.hideSpinner) c.hideSpinner()
            }
        },
        methods: {
            onAssinaturaGerada() {
                const titulo = 'Enviado para Assinatura!'
                const mensagem = 'O documento foi enviado para assinatura. ' +
                    'Você pode acompanhar o andamento na tela de Processos Inteligentes.'

                this.setFlowCompleted(titulo, mensagem, 'primary')
            },
            setFlowCompleted(title, message, variant = 'success') {
                this.flowCompleted = true
                this.flowCompletedTitle = title || ''
                this.flowCompletedMessage = message || ''
                this.flowCompletedVariant = ['success', 'primary'].includes(variant) ? variant : 'success'
                this.processoArtefato = null
            },

            irParaListaProcessos() {
                this.$router.push({ name: 'ProcessoInteligente' })
            },

            labelFromStep(step) {
                if (step === 'revisao') return 'Revisão'
                if (step === 'aprovacao') return 'Aprovação'
                if (step === 'assinatura') return 'Assinatura'
                if (step === 'finalizar') return 'o próximo artefato do fluxo'
                return 'a próxima etapa'
            },

            abrirModalAssinantes() {
                const el = document.getElementById('ModalEscolhaAssinatura');
                if (!el) return;

                const bs = window.bootstrap || window.Bootstrap || null;
                if (bs && bs.Modal) {
                    const modal = bs.Modal.getOrCreateInstance
                        ? bs.Modal.getOrCreateInstance(el)
                        : new bs.Modal(el);
                    modal.show();
                } else if (window.$ && typeof window.$.fn.modal === 'function') {
                    window.$(el).modal({ backdrop: 'static', keyboard: false, show: true });
                }
            },

            load() {
                this.getProcessoArtefato()
            },

            async getProcessoArtefato() {
                this.loading = true
                this.errorMsg = null
                try {
                    const id = this.$route.params.id
                    const { data } = await axios.get(
                        `${urlApi}/ProcessoArtefato/GetProcessoArtefatoEtapa`,
                        { params: { idProcessoArtefato: id } }
                    )

                    if (data?.success) {
                        const dto = Array.isArray(data.data) ? data.data[0] : data.data
                        if (!dto) { this.setError('Artefato não encontrado.'); return }
                        this.processoArtefato = dto
                        if (!dto.idProcessoArtefato) {
                            dto.idProcessoArtefato = Number(this.$route.params.id)
                        }
                        this.$nextTick(() => {
                            if (this.isDocxView) {
                                const url = this.isRevisado
                                    ? dto?.urlArquivoRevisado
                                    : dto?.urlArquivoEdicao

                                this.abrirDocumento(url)
                            } else {
                                if (this.isPdfView) {
                                const url = (this.isRevisado || this.isAprovado)
                                    ? dto?.urlArquivoRevisado
                                    : dto?.urlArquivo
                                this.carregarPdfSomenteLeitura(url)
                                } else {
                                    const msg = data?.msg || 'Erro ao carregar artefato.'
                                    this.setError(msg)
                                    avisoErro(msg)
                                    avisoToast('Não foi possível abrir o artefato.', 'warning')
                                }
                            }
                        })
                    } else {
                        const msg = data?.msg || 'Erro ao carregar artefato.'
                        this.setError(msg)
                        avisoErro(msg)
                        avisoToast('Não foi possível abrir o artefato.', 'warning')
                    }
                } catch (err) {
                    const payload = err.response?.data
                    const msg = (typeof payload === 'string' ? payload : payload?.msg) || err.message || 'Erro desconhecido.'
                    this.setError(msg)
                    avisoErro(msg)
                    avisoToast('Erro ao carregar artefato!', 'warning')
                } finally {
                    this.loading = false
                }
            },

            async abrirDocumento(url) {
                if (!url) { this.setError('URL do artefato vazia.'); return }

                const ext = (url.split('?')[0].split('#')[0].split('.').pop() || '').toLowerCase()
                if (ext === 'pdf') {
                    this.setError('Este artefato está em PDF. Use o visualizador de PDF para revisão.')
                    return
                }

                this.editorBusy = true
                if (this.editor) this.editor.isReadOnly = true

                try {
                    const { data: sfdt } = await axios.post(`${urlApi}/DOCXEditor/ImportFromUrl`, { url })
                    this.editor?.open(sfdt)
                    avisoToast('Documento carregado.', 'success')
                } catch (e) {
                    this.editorBusy = false
                    this.setError(e?.response?.data || e.message || 'Erro ao abrir documento no editor.')
                }
            },

            garantirTrackChanges() {
                const container = this.$refs.docx?.ej2Instances
                const ed = this.editor
                const nome = this.nomeUsuarioEditor
                if (ed) {
                    ed.enableTrackChanges = true
                    if (nome) ed.currentUser = nome
                }
                if (container) {
                    container.enableTrackChanges = true
                    if (nome) container.currentUser = nome
                }
            },

            onTrackChange() {
                this.$nextTick(() => {
                    if (this.editor && !this.editor.enableTrackChanges) {
                        this.garantirTrackChanges()
                    }
                })
            },

            onBeforeAcceptRejectChanges(args) {
                if (this.consolidandoRevisoes) return
                const author = args?.author || ''
                // Bloqueia aceitar/rejeitar revisões do próprio papel; sem sufixo (legado) libera
                if (author.endsWith(this.sufixoPapelEditor)) {
                    args.cancel = true
                }
            },

            // O PDF final não pode sair com marcações, então a consolidação ignora a regra de papel
            consolidarRevisoes() {
                const revisoes = this.editor?.revisions
                if (!revisoes || revisoes.length === 0) return

                this.consolidandoRevisoes = true
                try {
                    revisoes.acceptAll()
                } finally {
                    this.consolidandoRevisoes = false
                }
            },

            onEditorCreated() {
                const ed = this.editor
                if (!ed) return

                this.garantirTrackChanges()

                ed.documentChange = () => {
                    // Track changes é por documento; reativa após open
                    this.garantirTrackChanges()
                    if (this.editorBusy) {
                        ed.isReadOnly = false
                        this.editorBusy = false
                        ed.focusIn()
                    }
                }
            },

            onToolbarClick(args) {
                const txt = (args?.item?.text || '').toLowerCase()
                const id = (args?.item?.id || '').toLowerCase()
                if (txt === 'novo' || txt === 'abrir' || id.includes('new') || id.includes('open')) {
                    args.cancel = true
                    return
                }

                if (txt === 'salvar' || txt === 'save' || id.includes('save')) {
                    args.cancel = true
                    if (this.editorBusy) {
                        avisoToast('Aguarde o documento terminar de carregar…', 'warning')
                        return
                    }
                    this.salvarEdicao()
                    return
                }
                if (this.editorBusy) {
                    args.cancel = true
                    avisoToast('Aguarde o documento terminar de carregar…', 'warning')
                }
            },

            beforeSend(args) {
                const raw = this.$store?.state?.authToken || localStorage.getItem('authToken')
                const auth = raw?.startsWith('Bearer ') ? raw : (raw ? `Bearer ${raw}` : '')
                if (auth) args.headers = [{ Authorization: auth }]
                args.withCredentials = false
            },

            setError(msg) {
                this.errorMsg = msg
                this.processoArtefato = null
            },

            formatarData(iso) {
                try { return new Date(iso).toLocaleString() } catch { return iso }
            },

            progressoStatusPayload() {
                const dto = this.processoArtefato
                if (!dto?.idProcesso || !dto?.idProcessoModeloArtefato)
                    throw new Error('IDs do processo/artefato ausentes no DTO.')
                return { idProcesso: dto.idProcesso, idProcessoModeloArtefato: dto.idProcessoModeloArtefato }
            },

            async postAcao(path, { withUrl = false, url = null, reload = true } = {}) {
                const body = this.progressoStatusPayload()
                const config = withUrl ? { params: { urlArquivoRevisado: url } } : undefined
                const { data } = await axios.post(
                    `${urlApi}/ProcessoArtefatoStatusHistorico/${path}`,
                    body,
                    config
                )
                if (!data?.success) {
                    throw new Error(data?.msg || 'Operação não concluída.')
                }
                if (reload) {
                    await this.getProcessoArtefato()
                }
                return data
            },

            async darContinuidade() {
                const step = this.nextStep;

                if (step === 'finalizar') {
                    await this.finalizarArtefato();
                    return;
                }

                if (step === 'revisao' || step === 'aprovacao') {
                    this.modalEnvolvidosModo = step;
                    this.$refs.modalEnvolvidos?.abrir();
                }
            },

            async onEnvolvidosConfirm(payload) {
                const step = this.modalEnvolvidosModo
                const successTitle = step === 'aprovacao' ? 'Enviado para Aprovação!' : 'Enviado para Revisão!'
                try {
                    this.loading = true
                    await this.prepararDocumentoEncaminhamento(step)

                    const { data } = await axios.post(`${urlApi}/ProcessoArtefatoEnvolvido/SalvarEAvancar`, payload)
                    if (!data?.success) {
                        throw new Error(data?.msg || 'Não foi possível encaminhar o artefato.')
                    }

                    this.$refs.modalEnvolvidos?.fechar()

                    const variant = step === 'aprovacao' ? 'primary' : 'success'
                    const message = step === 'revisao'
                        ? 'O documento foi salvo e enviado para Revisão. Você pode acompanhar o andamento na tela de Processos Inteligentes.'
                        : 'O documento foi salvo e enviado para Aprovação. Você pode acompanhar o andamento na tela de Processos Inteligentes.'
                    this.setFlowCompleted(successTitle, message, variant)
                } catch (e) {
                    if (e?.response?.status === 1001) {
                        this.setError('Este artefato já está em continuidade.')
                        return
                    }
                    avisoErro(e?.response?.data?.msg || e?.message || 'Falha ao encaminhar o artefato.')
                } finally {
                    this.loading = false
                }
            },

            async prepararDocumentoEncaminhamento(step) {
                if (this.isDocxView) {
                    if (step === 'revisao' || step === 'aprovacao') {
                        await this.salvarEdicaoInterno({ showToast: false })
                    }
                    const blob = await this.exportDocxBlob()
                    const mime = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    return await this.uploadToS3(blob, mime, 'ProcessoArtefatoRevisao', 'docx')
                }

                if (step === 'revisao' || step === 'aprovacao') {
                    await this.salvarEdicaoInternoPDF({ showToast: false })
                }
                return this.processoArtefato?.urlArquivo
            },

            async uploadToS3(blob, contentType, destino, ext) {
                const dto = this.processoArtefato || {}
                const uploaded = await uploadFileToS3(blob, {
                    destino,
                    fileName: `artefato.${ext}`,
                    contentType,
                    idProcesso: dto.idProcesso,
                    idProcessoModeloArtefato: dto.idProcessoModeloArtefato,
                })
                return uploaded.key
            },

            async salvarEdicaoInterno({ showToast = true } = {}) {
                if (!this.isDocxView) {
                    throw new Error('Este documento está em modo somente leitura.')
                }

                if (!this.editor) {
                    throw new Error('Editor não inicializado.')
                }

                const dto = this.processoArtefato || {}
                if (!dto.idProcessoArtefato) {
                    throw new Error('ID do artefato não encontrado.')
                }
                const blob = await this.exportDocxBlob()
                const mime = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

                const key = await this.uploadToS3(blob, mime, 'ProcessoArtefatoEdicao', 'docx')
                const payload = {
                    idProcessoArtefato: dto.idProcessoArtefato,
                    urlArquivoEdicao: key,
                }
                const { data } = await axios.post(`${urlApi}/ProcessoArtefato/SalvarEdicao`, payload)
                if (!data?.success) {
                    throw new Error(data?.msg || 'Não foi possível salvar a edição.')
                }

                this.processoArtefato.urlArquivoEdicao = key

                if (showToast) {
                    avisoToast('Alterações salvas com sucesso.', 'success')
                }

                return key
            },

            async salvarEdicaoInternoPDF({ showToast = true } = {}) {

                const dto = this.processoArtefato || {}
                if (!dto.idProcessoArtefato) {
                    throw new Error('ID do artefato não encontrado.')
                }
                const payload = {
                    idProcessoArtefato: dto.idProcessoArtefato,
                    urlArquivoEdicao: dto.urlArquivo,
                }
                const { data } = await axios.post(`${urlApi}/ProcessoArtefato/SalvarEdicao`, payload)
                if (!data?.success) {
                    throw new Error(data?.msg || 'Não foi possível salvar a edição.')
                }

                this.processoArtefato.urlArquivoEdicao = dto.urlArquivo

                if (showToast) {
                    avisoToast('Alterações salvas com sucesso.', 'success')
                }

                return dto.urlArquivo
            },

            async salvarEdicao() {
                try {
                    this.loading = true
                    await this.salvarEdicaoInterno({ showToast: true })
                } catch (e) {
                    console.error(e)
                    avisoErro(e?.message || 'Erro ao salvar alterações.')
                } finally {
                    this.loading = false
                }
            },

            async prepararEEnviarParaAssinatura() {
                try {
                    this.loading = true;

                    if (!this.isDocxView) {
                        this.abrirModalAssinantes();
                        return;
                    }

                    if (!this.editor) {
                        throw new Error('Editor não inicializado.');
                    }

                    const dto = this.processoArtefato || {};
                    if (!dto.idProcessoArtefato) {
                        throw new Error('ID do artefato não encontrado.');
                    }

                    const blob = await this.exportSfdtAsPdfBlob();

                    const key = await this.uploadToS3(blob, 'application/pdf', 'ProcessoArtefatoRevisao', 'pdf');

                    const payload = {
                        idProcessoArtefato: dto.idProcessoArtefato,
                        urlArquivoRevisado: key,
                    };

                    const { data } = await axios.post(
                        `${this.urlApi}/ProcessoArtefato/SalvarParaAssinatura`,
                        payload
                    );

                    if (!data?.success && data?.success !== undefined) {
                        throw new Error(data?.msg || 'Não foi possível preparar o documento para assinatura.');
                    }

                    this.processoArtefato.urlArquivoRevisado = key;

                    avisoToast('Documento preparado para assinatura.', 'success');
                    this.abrirModalAssinantes();
                } catch (e) {
                    console.error(e);
                    avisoErro(e?.message || 'Erro ao preparar documento para assinatura.');
                } finally {
                    this.loading = false;
                }
            },


            async exportSfdtAsDataUrl(format = 'pdf') {
                if (!this.editor) throw new Error('Editor não inicializado.')
                const content = this.editor.serialize()
                const raw = this.$store?.state?.authToken || localStorage.getItem('authToken')
                const authHeader = raw?.startsWith('Bearer ') ? raw : (raw ? `Bearer ${raw}` : '')
                const { data: dataUrl } = await axios.post(
                    `${urlApi}/DOCXEditor/ExportBase64`,
                    { content, format, fileName: 'artefato' },
                    { headers: authHeader ? { Authorization: authHeader } : undefined }
                )
                if (!dataUrl || typeof dataUrl !== 'string') throw new Error('Falha ao exportar (retorno inválido).')
                return dataUrl
            },

            async exportSfdtAsPdfBlob() {
                if (!this.editor) throw new Error('Editor não inicializado.')

                this.consolidarRevisoes()

                const docxBlob = await this.exportDocxBlob()
                const raw = this.$store?.state?.authToken || localStorage.getItem('authToken')
                const authHeader = raw?.startsWith('Bearer ') ? raw : (raw ? `Bearer ${raw}` : '')

                const formData = new FormData()
                formData.append('file', docxBlob, 'artefato.docx')

                const { data: pdfBlob } = await axios.post(
                    `${urlApi}/DOCXEditor/ExportPdfFromBlob`,
                    formData,
                    {
                        headers: authHeader ? { Authorization: authHeader } : undefined,
                        responseType: 'blob'
                    }
                )

                if (!(pdfBlob instanceof Blob) || pdfBlob.size === 0) {
                    throw new Error('Falha ao exportar PDF (retorno inválido).')
                }

                return pdfBlob
            },

            async exportDocxBlob() {
                const ed = this.editor
                if (!ed) throw new Error('Editor não inicializado.')
                const blob = await ed.saveAsBlob('Docx')
                return blob
            },

            getExt(url) {
                if (!url || typeof url !== 'string') return ''
                return url.split('?')[0].split('#')[0].split('.').pop().toLowerCase()
            },

            dataUrlToBlob(dataUrl) {
                const [meta, base64] = dataUrl.split(',')
                const mime = (meta.match(/data:(.*?);base64/) || [])[1] || 'application/octet-stream'
                const bytes = atob(base64)
                const buf = new Uint8Array(bytes.length)
                for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i)
                return new Blob([buf], { type: mime })
            },

            async salvarRevisadoEChamar(pathController, formato = 'pdf') {
                let blob, mime, key

                if (formato === 'docx') {
                    blob = await this.exportDocxBlob()
                    mime = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    key = await this.uploadToS3(blob, mime, 'ProcessoArtefatoRevisao', 'docx')
                } else {
                    if (formato === 'pdf') {
                        blob = await this.exportSfdtAsPdfBlob()
                    } else {
                        const dataUrl = await this.exportSfdtAsDataUrl(formato)
                        blob = this.dataUrlToBlob(dataUrl)
                    }
                    mime = formato === 'pdf'
                        ? 'application/pdf'
                        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    const ext = formato === 'pdf' ? 'pdf' : 'docx'
                    key = await this.uploadToS3(blob, mime, 'ProcessoArtefatoRevisao', ext)
                }
                await this.postAcao(pathController, { withUrl: true, url: key, reload: false })
                avisoToast('Documento revisado salvo com sucesso.', 'success')
                return key
            },

            async salvarRevisadoEChamarPDF(pathController,urlArquivo ) {
                await this.postAcao(pathController, { withUrl: true, url: urlArquivo, reload: false })
                avisoToast('Documento revisado salvo com sucesso.', 'success')
                return urlArquivo
            },

            tentarNovamente() {
                this.getProcessoArtefato()
            },
            async carregarPdfSomenteLeitura(url) {
                if (!url) { this.setError('URL do artefato vazia.'); return }

                if (!this.$refs.pdfviewer) return

                const raw = this.$store?.state?.authToken || localStorage.getItem('authToken')
                const authHeader = raw?.startsWith('Bearer ') ? raw : (raw ? `Bearer ${raw}` : '')

                this.viewer = new PdfViewer({
                    serviceUrl: `${urlApi}/PdfViewer`,
                    locale: 'pt-BR',
                    ajaxRequestSettings: {
                        withCredentials: false,
                        headers: authHeader ? { Authorization: authHeader } : {}
                    },
                    // Block all modifications
                    enableAnnotation: false,
                    enableAnnotationToolbar: false,
                    enableTextMarkupAnnotation: false,
                    enableShapeAnnotation: false,
                    enableStampAnnotations: false,
                    enableFreeText: false,
                    enableHandwrittenSignature: false,
                    enableTextSearch: false,
                    enableFormFields: false,
                    enableCommentPanel: false,
                    toolbarSettings: {
                        showTooltip: true,
                        toolbarItems: ['PageNavigationTool', 'PanTool', 'ZoomInTool', 'ZoomOutTool', 'FitPageTool', 'PrintOption', 'DownloadOption']
                    },
                    height: '100%',
                    width: '100%',
                })

                this.viewer.appendTo(this.$refs.pdfviewer)

                try {
                    const { data: base64 } = await axios.get(`${urlApi}/PdfViewer/FetchAsBase64`, {
                        params: { url }
                    })
                    this.viewer.load(base64, null)
                } catch (err) {
                    this.setError('Não foi possível carregar o PDF aprovado.')
                }
            },

            async finalizarArtefato() {
                try {
                    const dto = this.progressoStatusPayload();
                    this.loading = true;

                    const confirm = await Swal.fire({
                        icon: 'question',
                        title: 'Finalizar Artefato?',
                        html: `<div style="text-align:left; line-height:1.5;">
                     <p>Esta ação irá <b>finalizar</b> o artefato atual e acionar o próximo artefato do fluxo, se houver.</p>
                     <p style="margin-top:10px;color:#6b7280;">Deseja continuar?</p>
                   </div>`,
                        showCancelButton: true,
                        confirmButtonText: 'Sim, finalizar',
                        cancelButtonText: 'Cancelar',
                        confirmButtonColor: '#d33',
                        cancelButtonColor: '#6b7280',
                        reverseButtons: true,
                    });

                    if (!confirm.isConfirmed) return;

                    const { data } = await axios.post(`${this.urlApi}/ProcessoArtefatoStatusHistorico/FinalizarArtefato`, dto);

                    if (data?.success) {
                        await Swal.fire({
                            icon: 'success',
                            title: 'Artefato Finalizado!',
                            text: 'O artefato foi finalizado e o próximo já foi acionado (caso aplicável).',
                            confirmButtonText: 'OK',
                            confirmButtonColor: '#16a34a'
                        });

                        this.setFlowCompleted(
                            'Artefato Finalizado!',
                            'O artefato foi finalizado e o próximo já foi acionado (caso aplicável). Você pode retornar à tela de Processos para acompanhar o fluxo.',
                            'success'
                        )
                    } else {
                        throw new Error(data?.msg || 'Falha ao finalizar artefato.');
                    }
                } catch (e) {
                    console.error(e);
                    avisoErro(e?.message || 'Erro ao finalizar artefato.');
                } finally {
                    this.loading = false;
                }
            }
        },

        mounted() {
            this.load()
        },
        beforeDestroy() {
        }
    }
</script>

<style scoped>
    /* Esconde os dois itens (match por aria-label e title em PT-BR) */
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-toolbar-item[aria-label^="Crie um novo documento"]),
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-toolbar-item[title^="Crie um novo documento"]),
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-toolbar-item[aria-label^="Abra um documento"]),
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-toolbar-item[title^="Abra um documento"]) {
        display: none !important;
    }

    /* Versão mais genérica (case-insensitive) — use se mudar o texto */
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-toolbar-item[aria-label*="novo documento" i]),
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-toolbar-item[title*="novo documento" i]),
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-toolbar-item[aria-label*="abra um documento" i]),
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-toolbar-item[title*="abra um documento" i]) {
        display: none !important;
    }

    /* Some com o separador imediatamente antes desses itens (suporta e-separator e e-tbar-separator) */
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-separator:has(+ .e-toolbar-item[aria-label^="Crie um novo documento"])),
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-separator:has(+ .e-toolbar-item[aria-label^="Abra um documento"])),
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-tbar-separator:has(+ .e-toolbar-item[aria-label^="Crie um novo documento"])),
    :deep(.e-documenteditorcontainer .e-de-toolbar .e-tbar-separator:has(+ .e-toolbar-item[aria-label^="Abra um documento"])) {
        display: none !important;
    }

    .blocker-success {
        border-color: #16a34a;
        color: #065f46;
    }

    .completion-container {
        position: relative;
        height: calc(100vh - var(--appbar-height));
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 32px;
        overflow: hidden;
    }

    .completion-bg {
        position: absolute;
        inset: 0;
        background: radial-gradient(circle at 20% 20%, rgba(56, 189, 248, 0.25), transparent 55%),
                    radial-gradient(circle at 80% 30%, rgba(16, 185, 129, 0.35), transparent 60%),
                    radial-gradient(circle at 50% 80%, rgba(59, 130, 246, 0.3), transparent 55%);
        filter: blur(60px);
        opacity: 0.65;
        transform: scale(1.2);
        pointer-events: none;
    }

    .completion-card {
        position: relative;
        display: flex;
        align-items: center;
        gap: 36px;
        padding: 36px 40px;
        border-radius: 28px;
        box-shadow: 0 30px 70px rgba(15, 23, 42, 0.12);
        backdrop-filter: blur(12px);
        color: #0f172a;
        overflow: hidden;
        max-width: 820px;
        width: 100%;
        animation: fadeUp 0.5s ease-out;
    }

    .completion-card::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        border: 1px solid rgba(255, 255, 255, 0.35);
        pointer-events: none;
    }

    .completion-card-success {
        background: linear-gradient(135deg, rgba(110, 231, 183, 0.25), rgba(5, 150, 105, 0.4));
    }

    .completion-card-primary {
        background: linear-gradient(135deg, rgba(96, 165, 250, 0.25), rgba(37, 99, 235, 0.45));
    }

    .completion-icon {
        position: relative;
        flex-shrink: 0;
        width: 120px;
        height: 120px;
        border-radius: 50%;
        display: grid;
        place-items: center;
        background: rgba(255, 255, 255, 0.45);
        color: #0f172a;
        box-shadow: 0 20px 45px rgba(15, 23, 42, 0.15);
    }

    .completion-icon svg {
        width: 60px;
        height: 60px;
    }

    .completion-spark {
        position: absolute;
        inset: 8px;
        border-radius: inherit;
        border: 1px solid rgba(15, 23, 42, 0.08);
        animation: pulse 2.8s ease-in-out infinite;
    }

    .completion-content {
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    .completion-content h3 {
        font-size: 2rem;
        font-weight: 700;
        color: #0f172a;
        margin: 0;
        line-height: 1.2;
    }

    .completion-content p {
        font-size: 1rem;
        line-height: 1.6;
        color: rgba(15, 23, 42, 0.75);
        margin: 0;
        max-width: 520px;
    }

    .completion-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
    }

    .completion-actions button {
        min-width: 190px;
    }

    .btn-solid,
    .btn-outline {
        border-radius: 999px;
        padding: 12px 24px;
        font-weight: 600;
        font-size: 0.95rem;
        border: none;
        cursor: pointer;
        transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
        box-shadow: 0 14px 30px rgba(15, 23, 42, 0.15);
    }

    .btn-solid {
        background: linear-gradient(135deg, #16a34a, #0f766e);
        color: #fff;
    }

    .btn-solid:hover {
        transform: translateY(-2px);
        box-shadow: 0 18px 40px rgba(15, 23, 42, 0.2);
        background: linear-gradient(135deg, #15803d, #0d9488);
    }

    .btn-outline {
        background: rgba(255, 255, 255, 0.9);
        color: #0f172a;
        border: 1px solid rgba(15, 23, 42, 0.1);
        backdrop-filter: blur(6px);
    }

    .btn-outline:hover {
        transform: translateY(-2px);
        box-shadow: 0 18px 40px rgba(15, 23, 42, 0.16);
        background: rgba(255, 255, 255, 0.98);
    }

    .completion-card-primary .btn-solid {
        background: linear-gradient(135deg, #2563eb, #1d4ed8);
    }

    .completion-card-primary .btn-solid:hover {
        background: linear-gradient(135deg, #1d4ed8, #1e40af);
    }

    @keyframes fadeUp {
        from {
            opacity: 0;
            transform: translateY(24px);
        }

        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    @keyframes pulse {
        0%, 100% {
            opacity: 0.35;
        }

        50% {
            opacity: 0.65;
        }
    }

    @media (max-width: 920px) {
        .completion-card {
            flex-direction: column;
            text-align: center;
            padding: 28px 24px 32px;
        }

        .completion-icon {
            width: 96px;
            height: 96px;
        }

        .completion-content {
            align-items: center;
        }

        .completion-content h3 {
            font-size: 1.7rem;
        }

        .completion-actions {
            justify-content: center;
        }

        .completion-actions button {
            flex: 1 1 140px;
        }
    }

    .docx-wrapper {
        flex: 3 1 0;
    }

    .pdf-wrapper {
        flex: 3 1 0;
    }

    .pdf-host {
        height: 100%;
        width: 100%;
        overflow: hidden;
    }

    .viewer-layout {
        display: flex;
        gap: 0;
        height: calc(100vh - var(--appbar-height));
    }

        .viewer-layout > * {
            min-width: 0;
            box-sizing: border-box;
        }

    .side-panel {
        flex: 1 1 0;
        max-width: 380px;
        min-width: 320px;
        padding: 24px 20px;
        display: flex;
        flex-direction: column;
        gap: 20px;
        overflow-y: auto;
        overflow-x: hidden;
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        border-left: 1px solid rgba(0, 0, 0, 0.08);
    }

    .panel-header {
        background: linear-gradient(135deg, #235FE8 0%, #00d084 100%);
        border-radius: 16px;
        padding: 20px;
        color: white;
        box-shadow: 0 8px 24px rgba(35, 95, 232, 0.25);
        display: flex;
        gap: 16px;
        align-items: flex-start;
        animation: slideInRight 0.4s ease-out;
    }

    .header-icon {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 12px;
        padding: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        backdrop-filter: blur(10px);
        flex-shrink: 0;
    }

        .header-icon svg {
            width: 24px;
            height: 24px;
            color: white;
        }

    .header-content {
        flex: 1;
        min-width: 0;
    }

    .titulo-artefato {
        margin: 0 0 8px 0;
        font-size: 1.25rem;
        font-weight: 700;
        line-height: 1.3;
        color: white;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .sub {
        margin: 0 0 10px 0;
        font-size: 0.9rem;
        opacity: 0.95;
        color: rgba(255, 255, 255, 0.95);
        line-height: 1.4;
    }

    .codigo-badge {
        display: inline-block;
        background: rgba(255, 255, 255, 0.25);
        padding: 4px 12px;
        border-radius: 20px;
        font-size: 0.8rem;
        font-weight: 600;
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.3);
    }

    .info-card {
        background: white;
        border-radius: 14px;
        padding: 18px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
        border: 1px solid rgba(0, 0, 0, 0.05);
        transition: all 0.3s ease;
        animation: slideInRight 0.4s ease-out;
        animation-fill-mode: both;
    }

        .info-card:hover {
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
            transform: translateY(-2px);
        }

    .status-card {
        animation-delay: 0.1s;
    }

    .details-card {
        animation-delay: 0.2s;
    }

    .card-title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 0.85rem;
        font-weight: 600;
        color: #6c757d;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 14px;
    }

        .card-title svg {
            opacity: 0.7;
        }

    .status-badge {
        display: inline-block;
        padding: 10px 18px;
        border-radius: 10px;
        font-weight: 600;
        font-size: 0.95rem;
        text-align: center;
        width: 100%;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease;
        background: linear-gradient(135deg, #a8e6cf 0%, #56ccf2 100%);
        color: #155724;
    }

    .status-revisao {
        background: linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%);
        color: #856404;
    }

    .status-aprovacao {
        background: linear-gradient(135deg, #a8e6cf 0%, #56ccf2 100%);
        color: #155724;
    }

    .info-grid {
        display: grid;
        gap: 14px;
    }

    .info-item {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 12px;
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        border-radius: 10px;
        border-left: 3px solid #235FE8;
        transition: all 0.3s ease;
    }

        .info-item:hover {
            border-left-color: #00d084;
            transform: translateX(2px);
        }

        .info-item .label {
            font-size: 0.8rem;
            font-weight: 600;
            color: #6c757d;
            text-transform: uppercase;
            letter-spacing: 0.3px;
        }

        .info-item .value {
            font-size: 0.95rem;
            font-weight: 600;
            color: #2d3748;
            line-height: 1.4;
        }

    .acoes-section {
        margin-top: auto;
        display: flex;
        flex-direction: column;
        gap: 12px;
        animation: slideInRight 0.4s ease-out;
        animation-delay: 0.3s;
        animation-fill-mode: both;
    }

    .acoes-title {
        font-size: 0.85rem;
        font-weight: 700;
        color: #495057;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 4px;
    }

    /* Botões de Ação */
    .btn-action {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 14px 20px;
        border: none;
        border-radius: 12px;
        font-size: 0.95rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        position: relative;
        overflow: hidden;
        color: white;
    }

        .btn-action::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 220%;
            height: 220%;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.3);
            transform: translate(-50%, -50%) scale(0);
            transition: transform 0.6s ease;
            pointer-events: none;
        }

        .btn-action:hover::before {
            transform: translate(-50%, -50%) scale(1);
        }

        .btn-action:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none !important;
        }

        .btn-action:not(:disabled):hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
        }

        .btn-action:not(:disabled):active {
            transform: translateY(0);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .btn-action svg {
            flex-shrink: 0;
            position: relative;
            z-index: 1;
        }

        .btn-action span {
            flex: 1;
            text-align: center;
            position: relative;
            z-index: 1;
        }

    .btn-primary {
        background: linear-gradient(135deg, #235FE8 0%, #00d084 100%);
        box-shadow: 0 4px 12px rgba(35, 95, 232, 0.3);
    }

        .btn-primary:not(:disabled):hover {
            background: linear-gradient(135deg, #1e50c9 0%, #00b872 100%);
            box-shadow: 0 8px 24px rgba(35, 95, 232, 0.4);
        }

        .btn-primary:not(:disabled):active {
            background: linear-gradient(135deg, #1a47b5 0%, #009f65 100%);
        }

    .btn-outline-primary {
        background: transparent;
        border: 2px solid #235FE8;
        color: #235FE8;
        box-shadow: 0 2px 8px rgba(35, 95, 232, 0.15);
    }

        .btn-outline-primary:not(:disabled):hover {
            background: rgba(35, 95, 232, 0.1);
            border-color: #1e50c9;
            color: #1e50c9;
            box-shadow: 0 4px 16px rgba(35, 95, 232, 0.25);
        }

        .btn-outline-primary:not(:disabled):active {
            background: rgba(35, 95, 232, 0.2);
            border-color: #1a47b5;
            color: #1a47b5;
        }

    .btn-success {
        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    }

        .btn-success:not(:disabled):hover {
            background: linear-gradient(135deg, #0ea374 0%, #047857 100%);
            box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4);
        }

        .btn-success:not(:disabled):active {
            background: linear-gradient(135deg, #0c8d63 0%, #036547 100%);
        }

    .btn-danger {
        background: linear-gradient(135deg, #f43f5e 0%, #e11d48 100%);
        box-shadow: 0 4px 12px rgba(244, 63, 94, 0.3);
    }

        .btn-danger:not(:disabled):hover {
            background: linear-gradient(135deg, #e11d48 0%, #be123c 100%);
            box-shadow: 0 8px 24px rgba(244, 63, 94, 0.4);
        }

        .btn-danger:not(:disabled):active {
            background: linear-gradient(135deg, #be123c 0%, #9f1239 100%);
        }

    .btn-warning {
        background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
    }

        .btn-warning:not(:disabled):hover {
            background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
            box-shadow: 0 8px 24px rgba(245, 158, 11, 0.4);
        }

        .btn-warning:not(:disabled):active {
            background: linear-gradient(135deg, #b45309 0%, #92400e 100%);
        }

    /* Animações */
    @keyframes slideInRight {
        from {
            opacity: 0;
            transform: translateX(20px);
        }

        to {
            opacity: 1;
            transform: translateX(0);
        }
    }

    /* Scrollbar customizada */
    .side-panel::-webkit-scrollbar {
        width: 6px;
    }

    .side-panel::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.05);
        border-radius: 10px;
    }

    .side-panel::-webkit-scrollbar-thumb {
        background: linear-gradient(135deg, #235FE8 0%, #00d084 100%);
        border-radius: 10px;
    }

        .side-panel::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(135deg, #1e50c9 0%, #00b872 100%);
        }

    .blocker {
        height: calc(100vh - var(--appbar-height));
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 24px;
        gap: 12px;
    }

        .blocker .motivo {
            max-width: 720px;
            opacity: .85;
            font-size: 0.95rem;
        }

        .blocker .btn {
            padding: 10px 16px;
            border-radius: 10px;
            border: none;
            cursor: pointer;
        }

    .editor-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(255, 255, 255, 0.95);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        backdrop-filter: blur(4px);
    }

    .overlay-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
    }

        .overlay-content p {
            font-size: 1.1rem;
            font-weight: 600;
            color: #235FE8;
            margin: 0;
        }

    .spinner {
        width: 50px;
        height: 50px;
        animation: rotate 2s linear infinite;
    }

        .spinner circle {
            stroke: #235FE8;
            stroke-linecap: round;
            animation: dash 1.5s ease-in-out infinite;
        }

    @keyframes rotate {
        100% {
            transform: rotate(360deg);
        }
    }

    @keyframes dash {
        0% {
            stroke-dasharray: 1, 150;
            stroke-dashoffset: 0;
        }

        50% {
            stroke-dasharray: 90, 150;
            stroke-dashoffset: -35;
        }

        100% {
            stroke-dasharray: 90, 150;
            stroke-dashoffset: -124;
        }
    }

    .is-loading {
        position: relative;
    }

    /* ========== DARK THEME - SIDE PANEL ONLY ========== */
    [data-bs-theme="dark"] .side-panel {
        background: linear-gradient(135deg, #1a1f2e 0%, #252b3d 100%);
        border-left: 1px solid rgba(255, 255, 255, 0.1);
    }

    [data-bs-theme="dark"] .completion-container {
        background: linear-gradient(135deg, rgba(15, 23, 42, 0.85) 0%, rgba(30, 41, 59, 0.95) 100%);
        color: #e2e8f0;
    }

    [data-bs-theme="dark"] .completion-card {
        box-shadow: 0 30px 70px rgba(15, 23, 42, 0.45);
        color: #e2e8f0;
    }

    [data-bs-theme="dark"] .completion-card-success {
        background: linear-gradient(135deg, rgba(34, 197, 94, 0.22), rgba(5, 150, 105, 0.38));
    }

    [data-bs-theme="dark"] .completion-card-primary {
        background: linear-gradient(135deg, rgba(37, 99, 235, 0.25), rgba(59, 130, 246, 0.4));
    }

    [data-bs-theme="dark"] .completion-content p {
        color: rgba(226, 232, 240, 0.78);
    }

    [data-bs-theme="dark"] .btn-outline {
        background: rgba(226, 232, 240, 0.12);
        color: #e2e8f0;
        border-color: rgba(148, 163, 184, 0.35);
    }

    [data-bs-theme="dark"] .btn-outline:hover {
        background: rgba(226, 232, 240, 0.24);
        box-shadow: 0 18px 40px rgba(37, 99, 235, 0.25);
    }

    [data-bs-theme="dark"] .panel-header {
        background: linear-gradient(135deg, #60a5fa 0%, #34d399 100%);
        box-shadow: 0 8px 24px rgba(96, 165, 250, 0.3);
    }

    [data-bs-theme="dark"] .header-icon {
        background: rgba(255, 255, 255, 0.25);
    }

    [data-bs-theme="dark"] .titulo-artefato,
    [data-bs-theme="dark"] .sub,
    [data-bs-theme="dark"] .codigo-badge {
        color: white;
    }

    [data-bs-theme="dark"] .codigo-badge {
        background: rgba(255, 255, 255, 0.2);
        border-color: rgba(255, 255, 255, 0.25);
    }

    [data-bs-theme="dark"] .info-card {
        background: linear-gradient(135deg, #252b3d 0%, #2d3548 100%);
        border-color: rgba(255, 255, 255, 0.1);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
    }

    [data-bs-theme="dark"] .info-card:hover {
        box-shadow: 0 8px 24px rgba(96, 165, 250, 0.2);
        border-color: rgba(96, 165, 250, 0.3);
    }

    [data-bs-theme="dark"] .card-title {
        color: #d1d5db;
    }

    [data-bs-theme="dark"] .status-badge {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
        background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
        color: #1f2937;
        font-weight: 700;
    }

    [data-bs-theme="dark"] .info-item {
        background: linear-gradient(135deg, #2d3548 0%, #353d52 100%);
        border-left-color: #60a5fa;
    }

    [data-bs-theme="dark"] .info-item:hover {
        border-left-color: #34d399;
        background: linear-gradient(135deg, #353d52 0%, #3d4558 100%);
    }

    [data-bs-theme="dark"] .info-item .label {
        color: #9ca3af;
    }

    [data-bs-theme="dark"] .info-item .value {
        color: #f3f4f6;
    }

    [data-bs-theme="dark"] .acoes-title {
        color: #d1d5db;
    }

    [data-bs-theme="dark"] .btn-action {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
    }

    [data-bs-theme="dark"] .btn-action:not(:disabled):hover {
        box-shadow: 0 8px 24px rgba(96, 165, 250, 0.3);
    }

    [data-bs-theme="dark"] .btn-primary {
        background: linear-gradient(135deg, #60a5fa 0%, #34d399 100%);
        box-shadow: 0 4px 12px rgba(96, 165, 250, 0.4);
    }

    [data-bs-theme="dark"] .btn-primary:not(:disabled):hover {
        background: linear-gradient(135deg, #3b82f6 0%, #10b981 100%);
        box-shadow: 0 8px 24px rgba(96, 165, 250, 0.5);
    }

    [data-bs-theme="dark"] .btn-primary:not(:disabled):active {
        background: linear-gradient(135deg, #2563eb 0%, #059669 100%);
    }

    [data-bs-theme="dark"] .btn-outline-primary {
        background: transparent;
        border: 2px solid #60a5fa;
        color: #60a5fa;
        box-shadow: 0 2px 8px rgba(96, 165, 250, 0.2);
    }

        [data-bs-theme="dark"] .btn-outline-primary:not(:disabled):hover {
            background: rgba(96, 165, 250, 0.15);
            border-color: #3b82f6;
            color: #3b82f6;
            box-shadow: 0 4px 16px rgba(96, 165, 250, 0.3);
        }

        [data-bs-theme="dark"] .btn-outline-primary:not(:disabled):active {
            background: rgba(96, 165, 250, 0.25);
            border-color: #2563eb;
            color: #2563eb;
        }

    [data-bs-theme="dark"] .btn-success {
        background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
        box-shadow: 0 4px 12px rgba(52, 211, 153, 0.4);
    }

    [data-bs-theme="dark"] .btn-success:not(:disabled):hover {
        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        box-shadow: 0 8px 24px rgba(52, 211, 153, 0.5);
    }

    [data-bs-theme="dark"] .btn-success:not(:disabled):active {
        background: linear-gradient(135deg, #059669 0%, #047857 100%);
    }

    [data-bs-theme="dark"] .btn-danger {
        background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
        box-shadow: 0 4px 12px rgba(248, 113, 113, 0.4);
    }

    [data-bs-theme="dark"] .btn-danger:not(:disabled):hover {
        background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        box-shadow: 0 8px 24px rgba(248, 113, 113, 0.5);
    }

    [data-bs-theme="dark"] .btn-danger:not(:disabled):active {
        background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    }

    [data-bs-theme="dark"] .btn-warning {
        background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
        box-shadow: 0 4px 12px rgba(251, 191, 36, 0.4);
    }

    [data-bs-theme="dark"] .btn-warning:not(:disabled):hover {
        background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        box-shadow: 0 8px 24px rgba(251, 191, 36, 0.5);
    }

    [data-bs-theme="dark"] .btn-warning:not(:disabled):active {
        background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
    }

    [data-bs-theme="dark"] .side-panel::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
    }

    [data-bs-theme="dark"] .side-panel::-webkit-scrollbar-thumb {
        background: linear-gradient(135deg, #60a5fa 0%, #34d399 100%);
    }

    [data-bs-theme="dark"] .side-panel::-webkit-scrollbar-thumb:hover {
        background: linear-gradient(135deg, #3b82f6 0%, #10b981 100%);
    }

    [data-bs-theme="dark"] .blocker {
        background: linear-gradient(135deg, #1a1f2e 0%, #252b3d 100%);
        color: #f3f4f6;
    }

    [data-bs-theme="dark"] .blocker h3 {
        color: #ffffff;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }

    [data-bs-theme="dark"] .blocker .motivo {
        color: #d1d5db;
    }

    [data-bs-theme="dark"] .blocker .btn {
        background: linear-gradient(135deg, #60a5fa 0%, #34d399 100%);
        color: white;
        box-shadow: 0 4px 12px rgba(96, 165, 250, 0.3);
        transition: all 0.3s ease;
    }

    [data-bs-theme="dark"] .blocker .btn:hover {
        background: linear-gradient(135deg, #3b82f6 0%, #10b981 100%);
        box-shadow: 0 8px 24px rgba(96, 165, 250, 0.4);
        transform: translateY(-2px);
    }

    [data-bs-theme="dark"] .editor-overlay {
        background: rgba(26, 31, 46, 0.95);
    }

    [data-bs-theme="dark"] .overlay-content p {
        color: #60a5fa;
    }

    [data-bs-theme="dark"] .spinner circle {
        stroke: #60a5fa;
    }
</style>
